/**
 * # Footer
 *
 * React component that represents the footer section of Albert.
 *
 * ## Props
 *
 * - `className` (string): Additional CSS class name for the footer.
 * - `minimal` (boolean): Flag to indicate if the footer should be rendered in minimal mode.
 */
import React from 'react';

import classnames from 'classnames';
import moment from 'moment';
import constants from 'client/constants';
import Logo from '@albert-io/atomic/atoms/Logo/Logo.react';

import {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';

import Divider from '../../atoms/Divider/Divider.react';
import Anchor from '../../atoms/Anchor/Anchor.react';
import Text from '../../atoms/Text/Text.react';

import {FooterColumn, FooterDataItem, getFooterData} from './FooterData';

import './footer.scss';

interface FooterProps extends PropsWithClassNameOptional {
  minimal?: boolean;
}

const Footer = ({className, minimal}: FooterProps) => (
  <footer className={classnames('footer', className)} role='contentinfo'>
    {!minimal && (
      <>
        <div className='footer-logo-image__wrapper u-mar-b_4 u-pad-t_5'>
          <Logo />
        </div>
        <div className='footer__columns'>
          {getFooterData().map((column) => (
            <Column key={column.key} title={column.title} items={column.items} />
          ))}
        </div>
        <div className='footer-social-media'>
          <Anchor
            icon={['fab', 'facebook-f']}
            href='https://www.facebook.com/learnwithalbert/'
            rel='noopener noreferrer'
            target='_blank'
            className='u-mar-lr_2 footer-media'
          >
            Facebook
          </Anchor>
          <Anchor
            icon={['fab', 'twitter']}
            href='https://twitter.com/learnwithalbert'
            rel='noopener noreferrer'
            target='_blank'
            className='u-mar-lr_2 footer-media'
          >
            Twitter
          </Anchor>
        </div>
        <div>
          <Divider spacing={4} />
        </div>
      </>
    )}
    <div className='footer-legal'>
      <Text as='p' size='m' color='tertiary'>
        Copyright {moment().year()} Learn By Doing, Inc.
        <Anchor href='/privacy' className='u-mar-l_2 content-tertiary-inverse'>
          Privacy Policy
        </Anchor>
        <Anchor href='/terms-of-use' className='u-mar-l_2 content-tertiary-inverse'>
          Terms of Use
        </Anchor>
      </Text>

      <Text
        as='p'
        size='m'
        color='tertiary'
        className='u-mar-b_0 u-mar-t_2 footer-legal__disclaimer'
      >
        {constants.FULL_DISCLAIMER}
      </Text>
    </div>
  </footer>
);

const Column = ({title, items}: FooterColumn) => {
  return (
    <div className='footer-column u-mar-b_7'>
      <Text bold color='secondary' className='u-mar-b_2' size='m' as='p'>
        {title}
      </Text>
      <ul className='footer-column__list'>
        {items.map((item: FooterDataItem) => {
          const {label, link, icon, type} = item;
          return type === 'title' ? (
            <Text bold color='secondary' className='u-mar-b_2 u-mar-t_3' size='m' as='p'>
              {label}
            </Text>
          ) : (
            <Anchor as='a' className='footer-item u-mar-b_2' href={link} icon={icon as IconProp}>
              {label}
            </Anchor>
          );
        })}
      </ul>
    </div>
  );
};

export default Footer;
